'use client';

import { useState } from 'react';

import { useBranchDetail } from '@/features/lead-form/hooks/useBranchDetail';
import { useBranchFiles } from '@/features/location/hooks/useBranchFiles';
import { $api } from '@/shared/apis';
import { GTM_EVENT } from '@/shared/constants/gtm';
import { downloadFile } from '@/shared/utils/downloadFile';
import { pushGTMEvent, toBtnName } from '@/shared/utils/gtm';
import { Button } from '@publishing/components/ui/Button';
import Icon from '@publishing/components/ui/Icon';
import {
  ActionDownload,
  EtcConfirm,
} from '@publishing/components/ui/SvgSprite';

import Form from '../Form';
import { FormModeContext } from '../_context';
import FormLayerPopup from '../FormLayerPopup';
import popups from '../FormPopup.module.scss';
import PurposeGroup from '../common/PurposeGroup';
import {
  PDF_DOWNLOAD_CONFIG,
  type PdfDownloadCopy,
  type PdfDownloadName,
} from './data';
import {
  buildFormProps,
  resolveStaticPdfDownload,
  toPdfFileName,
} from './utils';
import s from './PdfDownloadPopup.module.scss';

type PdfDownloadPopupProps = {
  pdf: PdfDownloadName;
  btnName: string;
  onClose: () => void;
};

type Phase = 'form' | 'complete';

// ─────────────────────────────────────────────────────────
// 지점 데이터 조회 훅 — 정적 상품이 아닌 경우에만 API 호출
// ─────────────────────────────────────────────────────────

export function usePdfBranchList(pdf: PdfDownloadName | undefined) {
  return $api.useQuery(
    'get',
    '/api/v1/branches/shared-office',
    { params: { query: { page: 1, size: 200, sortBy: 'NAME' } } },
    { enabled: !!pdf && !resolveStaticPdfDownload(pdf) },
  );
}

function useBranchData(pdf: PdfDownloadName) {
  const branchList = usePdfBranchList(pdf);

  const branch = branchList.data?.branches.find((item) => item.displayName === pdf);
  const branchId = branch?.id ?? 0;

  const branchDetail = useBranchDetail(branchId);
  const branchFiles = useBranchFiles(branchId > 0 ? branchId : null);
  const apiFile = branchFiles.data?.files[0];

  return {
    download:
      branch && apiFile
        ? {
            category: 'branch' as const,
            name: branch.displayName,
            file: {
              fileUrl: apiFile.fileUrl,
              fileName: apiFile.fileName ?? toPdfFileName(branch.displayName),
            },
          }
        : undefined,
    maruBranchId: branchDetail.data?.maruBranchId,
  };
}

// ─────────────────────────────────────────────────────────
// 메인 컴포넌트
// ─────────────────────────────────────────────────────────

export function PdfDownloadPopup({ pdf, btnName, onClose }: PdfDownloadPopupProps) {
  const [phase, setPhase] = useState<Phase>('form');
  const [downloadPurpose, setDownloadPurpose] = useState<string[]>([]);

  const staticDownload = resolveStaticPdfDownload(pdf);
  const branchData = useBranchData(pdf);
  const download = staticDownload ?? branchData.download;

  const category = staticDownload ? 'product' : 'branch';
  const name = download?.name ?? pdf;
  const copy: PdfDownloadCopy = PDF_DOWNLOAD_CONFIG[category].copy;

  const formProps = buildFormProps({
    category,
    productName: staticDownload?.name,
    maruBranchId: branchData.maruBranchId,
    downloadPurpose,
  });

  const startDownload = async () => {
    if (!download) return;
    await downloadFile(download.file.fileUrl, download.file.fileName);
    pushGTMEvent(GTM_EVENT.DOWNLOAD, {
      btnname: toBtnName(btnName),
      brochure_name: toBtnName(name),
    });
    setPhase('complete');
  };

  return (
    <FormLayerPopup
      title={copy.popupTitle}
      onClose={onClose}
      className={s.popup}
    >
      {phase === 'form' ? (
        <FormModeContext.Provider value="popup">
          <Form
            formName={formProps.formName}
            beforeFields={
              <h2 className={s.header_title}>{copy.formHeader(name)}</h2>
            }
            leadType={formProps.leadType}
            productType={formProps.productType}
            gtmLocationName={category === 'branch' ? name : undefined}
            extraData={formProps.extraData}
            beforeAgreement={
              <PurposeGroup
                label="다운로드 목적 (중복 선택 가능)"
                value={downloadPurpose}
                onChange={setDownloadPurpose}
                className={s.purpose_group}
              />
            }
            submitLabel={copy.submitLabel ?? '다운로드'}
            submitIcon={
              copy.submitIcon ? (
                <Icon name={ActionDownload} size={20} />
              ) : undefined
            }
            extraValid={downloadPurpose.length > 0 && !!download}
            className={`${popups.form_area} ${s.form_area}`}
            actionAreaClassName={popups.form_action_area}
            onSuccess={() => void startDownload()}
          />
        </FormModeContext.Provider>
      ) : (
        <div className={s.complete}>
          <div className={s.complete_content}>
            <Icon
              name={EtcConfirm}
              theme="completed"
              size={56}
              className={s.complete_icon}
            />
            <div className={s.complete_text}>
              <h2 className={s.complete_title}>{copy.completeTitle(name)}</h2>
              <p className={s.complete_desc}>{copy.completeDesc}</p>
              {copy.completeNote && (
                <p className={s.complete_note}>{copy.completeNote}</p>
              )}
            </div>
          </div>
          <Button icon={false} onClick={onClose} className={s.complete_button}>
            {copy.completeButtonLabel ?? '닫기'}
          </Button>
        </div>
      )}
    </FormLayerPopup>
  );
}
